import { useEffect, useState } from "react";
import { Route, BrowserRouter, Routes } from "react-router-dom";
import { FirstSyncDialog } from "@/components/FirstSyncDialog";
import { Footer } from "@/components/Footer";
import { SearchOverlay } from "@/components/SearchOverlay";
import { TopBar } from "@/components/TopBar";
import { useActivity } from "@/hooks/useActivity";
import { useTheme } from "@/hooks/useTheme";
import { DayDetail } from "@/routes/DayDetail";
import { DesignSystem } from "@/routes/DesignSystem";
import { Flashcard } from "@/routes/Flashcard";
import { FillBlank } from "@/routes/FillBlank";
import { Home } from "@/routes/Home";
import { Privacy, Terms } from "@/routes/Legal";
import { NotFound } from "@/routes/NotFound";
import { Progress } from "@/routes/Progress";
import { Quiz } from "@/routes/Quiz";
import { Review } from "@/routes/Review";
import { Settings } from "@/routes/Settings";
import { Typing } from "@/routes/Typing";
import { WordDetail } from "@/routes/WordDetail";

export function App() {
  // Mount the theme hook once at the top so the `<html data-theme>` attribute
  // reflects the saved preference before any screen renders.
  useTheme();
  const { streak } = useActivity();
  const [searchOpen, setSearchOpen] = useState(false);

  // Global ⌘K / Ctrl+K and "/" shortcut to open the search palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === "k") {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (k === "/" && document.activeElement === document.body) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <BrowserRouter>
      <TopBar streak={streak} onSearch={() => setSearchOpen(true)} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/day/:day" element={<DayDetail />} />
        <Route path="/word/:day/:idx" element={<WordDetail />} />
        <Route path="/flashcard/:day" element={<Flashcard />} />
        <Route path="/quiz/:day" element={<Quiz />} />
        <Route path="/typing/:day" element={<Typing />} />
        <Route path="/fillblank/:day" element={<FillBlank />} />
        <Route path="/review" element={<Review />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/design-system" element={<DesignSystem />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <FirstSyncDialog />
    </BrowserRouter>
  );
}
